import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, apiPost, apiPut, apiPatch, fetchSchoolsSimple, fetchAllPaginated } from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import { normalizeText } from '../utils/normalizeText';

// Perfis cujo login é recortado por escola (`ROLES_COM_ESCOLA` no backend).
const PERFIS_COM_ESCOLA = ['supervisor', 'diretor', 'professor'];

// Acessos da linha que recebem a escola atribuída. A mesma pessoa pode ter
// um acesso por perfil (Diretor e Professor, por exemplo), todos ligados ao
// mesmo cadastro — a escola vale para cada um deles. Acesso inativo ou de
// perfil sem escola (Secretário) fica de fora: o backend recusaria.
const acessosComEscola = (row) => (row?.accounts || [])
  .filter(conta => conta.is_active !== false && PERFIS_COM_ESCOLA.includes(conta.role));

function GerentesEscolas() {
  const { user } = useAuth();
  const { darkMode } = useTheme();

  // Permissões: somente Admin e Coordenador atribuem escolas. Demais
  // perfis com acesso à tela (Referência Técnica, Supervisor, Gestor)
  // visualizam em modo leitura.
  const canEdit = user?.role === ROLES.ADMIN || user?.role === ROLES.COORDENADOR;

  // Estados
  // `employees` são os funcionários que podem receber turma — venham eles
  // com ou sem conta de acesso. A atribuição turma+disciplina é gravada no
  // CADASTRO do funcionário, então exigir login aqui só escondia professor.
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [busca, setBusca] = useState('');

  // Modal de atribuição — trabalha sobre uma "linha", que pode ser um
  // funcionário do RH (com ou sem login) ou uma conta de supervisor sem
  // cadastro de funcionário.
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedSchools, setSelectedSchools] = useState([]);
  // Para quem leciona: lista de { id_class, id_subject }
  const [teachingAssignments, setTeachingAssignments] = useState([]);
  // Cadastro de funcionário escolhido para uma conta que ainda não tem um.
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [saving, setSaving] = useState(false);

  const isProfessor = !!selectedRow?.canTeach;
  const effectiveTeacherId = selectedTeacherId || selectedRow?.employeeId || null;

  // Busca dados iniciais
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [employeesData, managersData, schoolsData, assignmentsData, classesData, subjectsData, classSubjectsData, teachersData] = await Promise.all([
        apiGet('/employees/assignable/'),
        apiGet('/users/supervisors/'),
        fetchSchoolsSimple(),
        apiGet('/user-school-assignments/by_manager/'),
        fetchAllPaginated('/classes/', { page_size: 500 }),
        fetchAllPaginated('/subjects/'),
        fetchAllPaginated('/class-subjects/', { is_active: true, page_size: 500 }),
        fetchAllPaginated('/teachers/', { page_size: 500 }),
      ]);

      setEmployees(Array.isArray(employeesData) ? employeesData : employeesData.results || []);
      setManagers(Array.isArray(managersData) ? managersData : managersData.results || []);
      setSchools(Array.isArray(schoolsData) ? schoolsData : schoolsData.results || []);
      setAssignments(Array.isArray(assignmentsData) ? assignmentsData : assignmentsData.results || []);
      setClasses(classesData);
      setSubjects(subjectsData);
      setClassSubjects(classSubjectsData);
      setTeachers(teachersData || []);
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Abre modal de atribuição
  const handleOpenModal = (row) => {
    setSelectedRow(row);
    setSelectedSchools((row.schools || []).map(e => e.id));
    setTeachingAssignments(
      (row.assignments || []).map(a => ({ id_class: a.id_class, id_subject: a.id_subject }))
    );
    setSelectedTeacherId(row.employeeId || '');
    setShowModal(true);
  };

  // Fecha modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRow(null);
    setSelectedSchools([]);
    setTeachingAssignments([]);
    setSelectedTeacherId('');
  };

  // Toggle seleção de escola
  const toggleSchoolSelection = (schoolId) => {
    setSelectedSchools(prev => {
      if (prev.includes(schoolId)) {
        // Remover atribuições turma/disciplina de turmas dessa escola
        setTeachingAssignments(prevTA =>
          prevTA.filter(ta => {
            const cls = classes.find(c => c.id === ta.id_class);
            return cls?.id_school !== schoolId;
          })
        );
        return prev.filter(id => id !== schoolId);
      }
      return [...prev, schoolId];
    });
  };

  // Turmas disponíveis (filtradas pelas escolas selecionadas)
  const availableClasses = useMemo(() => {
    if (!isProfessor || selectedSchools.length === 0) return [];
    return classes.filter(c => selectedSchools.includes(c.id_school));
  }, [classes, selectedSchools, isProfessor]);

  // Adiciona linha vazia de atribuição (turma, disciplina)
  const addTeachingRow = () => {
    setTeachingAssignments(prev => [...prev, { id_class: '', id_subject: '' }]);
  };

  const updateTeachingRow = (idx, field, value) => {
    setTeachingAssignments(prev =>
      prev.map((ta, i) => (i === idx ? { ...ta, [field]: value ? Number(value) : '' } : ta))
    );
  };

  const removeTeachingRow = (idx) => {
    setTeachingAssignments(prev => prev.filter((_, i) => i !== idx));
  };

  // Salva atribuições
  //
  // Escolas são gravadas em dois lugares diferentes de propósito:
  //   - no CADASTRO do funcionário (lotação do RH), que é o que faz a
  //     pessoa aparecer nas listas por cidade;
  //   - na CONTA de acesso (escopo do login), quando existe conta.
  // Turma + disciplina vai sempre para o cadastro do funcionário.
  const handleSaveAssignments = async () => {
    if (!selectedRow) return;

    if (selectedSchools.length === 0) {
      setError('Selecione pelo menos uma escola.');
      return;
    }

    if (isProfessor) {
      if (teachingAssignments.length > 0 && !effectiveTeacherId) {
        setError('Selecione um cadastro de funcionário para vincular antes de atribuir turmas/disciplinas.');
        return;
      }
      const invalid = teachingAssignments.some(ta => !ta.id_class || !ta.id_subject);
      if (invalid) {
        setError('Preencha turma e disciplina em todas as linhas (ou remova as vazias).');
        return;
      }
      const keys = teachingAssignments.map(ta => `${ta.id_class}-${ta.id_subject}`);
      if (new Set(keys).size !== keys.length) {
        setError('Há combinações duplicadas de turma e disciplina.');
        return;
      }
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const rowUser = selectedRow.user;
      const teacherIdToLink = selectedTeacherId ? Number(selectedTeacherId) : null;

      // Conta sem cadastro de funcionário: vincula o cadastro escolhido.
      // Só acontece nas linhas que vieram de /users/ — lá o objeto tem todos
      // os campos que o PUT exige; a linha de funcionário nunca passa aqui.
      if (selectedRow.kind === 'user' && rowUser && teacherIdToLink
          && teacherIdToLink !== (rowUser.id_teacher || null)) {
        await apiPut(`/users/${rowUser.id}/`, {
          email: rowUser.email,
          first_name: rowUser.first_name || '',
          last_name: rowUser.last_name || '',
          role: rowUser.role,
          city: rowUser.city || null,
          phone: rowUser.phone || '',
          is_active: rowUser.is_active !== false,
          id_teacher: teacherIdToLink,
        });
      }

      // Lotação no RH (tb_teacher_school)
      if (effectiveTeacherId) {
        await apiPatch(`/employees/${effectiveTeacherId}/`, { schools: selectedSchools });
      }

      // Escopo de acesso de cada login da pessoa
      for (const conta of acessosComEscola(selectedRow)) {
        await apiPost(`/users/${conta.id}/assign_schools/`, { schools: selectedSchools });
      }

      // Turma + disciplina: sempre pelo cadastro do funcionário
      if (isProfessor && effectiveTeacherId) {
        await apiPost('/class-subjects/bulk-set-by-teacher/', {
          id_teacher: effectiveTeacherId,
          items: teachingAssignments.map(ta => ({
            id_class: ta.id_class,
            id_subject: ta.id_subject,
          })),
        });
      }

      setSuccess(`Atribuições salvas com sucesso para ${selectedRow.name}!`);
      handleCloseModal();
      fetchData();
    } catch (err) {
      console.error('Erro ao salvar atribuições:', err);
      setError(err.response?.data?.error || err.message || 'Erro ao salvar atribuições. Verifique suas permissões.');
    } finally {
      setSaving(false);
    }
  };

  // Remove uma escola da linha (do cadastro do funcionário e/ou da conta)
  const handleRemoveSchool = async (row, schoolId) => {
    if (!window.confirm('Tem certeza que deseja remover esta escola?')) {
      return;
    }

    try {
      if (row.employeeId) {
        const restantes = (row.lotacao || row.schools || [])
          .map(e => e.id)
          .filter(id => id !== schoolId);
        await apiPatch(`/employees/${row.employeeId}/`, { schools: restantes });
      }
      for (const conta of acessosComEscola(row)) {
        try {
          await apiPost(`/users/${conta.id}/remove_school/`, { school_id: schoolId });
        } catch (err) {
          // 404: este acesso não tinha a escola — nada a remover nele.
          if (err?.status !== 404) throw err;
        }
      }

      setSuccess('Escola removida com sucesso!');
      fetchData();
    } catch (err) {
      console.error('Erro ao remover escola:', err);
      setError('Erro ao remover escola.');
    }
  };

  // Obtém escolas atribuídas a uma conta de acesso
  const getManagerSchools = (managerId) => {
    const assignment = assignments.find(a => a.user_id === managerId);
    return assignment?.schools || [];
  };

  // ------------------------------------------------------------------
  // Linhas da tela
  // ------------------------------------------------------------------
  // A lista principal são os FUNCIONÁRIOS que podem receber turma, tendo
  // login ou não. As contas de supervisor/professor que não têm cadastro de
  // funcionário continuam aparecendo à parte, para não perder nada.
  // Escolas da linha: a lotação no RH somada às escolas dos logins. Como
  // salvar agora grava a lista exata nos dois lugares, uma escola que só o
  // login tem precisa aparecer marcada — senão sairia sem ninguém ver.
  const employeeRows = useMemo(() => (
    employees.map((e) => {
      const accounts = e.users || (e.user ? [e.user] : []);
      const schools = [...(e.schools || [])];
      const vistas = new Set(schools.map(sc => sc.id));
      accounts.forEach(conta => getManagerSchools(conta.id).forEach(sc => {
        if (!vistas.has(sc.id)) {
          vistas.add(sc.id);
          schools.push({ id: sc.id, name: sc.school });
        }
      }));
      return {
        key: `emp-${e.id}`,
        kind: 'employee',
        employeeId: e.id,
        name: e.teacher_name,
        roleLabel: e.role_name || 'Funcionário',
        detail: [e.teacher_serial ? `Matrícula ${e.teacher_serial}` : null, e.email]
          .filter(Boolean)
          .join(' • '),
        user: e.user ? { ...e.user, id_teacher: e.id } : null,
        accounts,
        schools,
        lotacao: e.schools || [],
        assignments: e.assignments || [],
        canTeach: true,
      };
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [employees, assignments]);

  const userRows = useMemo(() => {
    const comCadastro = new Set(employees.map(e => e.id));
    return managers
      .filter(m => !m.id_teacher || !comCadastro.has(m.id_teacher))
      .map((m) => ({
        key: `user-${m.id}`,
        kind: 'user',
        employeeId: m.id_teacher || null,
        name: m.first_name && m.last_name ? `${m.first_name} ${m.last_name}` : m.username,
        roleLabel: { supervisor: 'Supervisor', diretor: 'Diretor(a)' }[m.role] || 'Professor',
        detail: [m.email, m.city_name].filter(Boolean).join(' • '),
        user: m,
        accounts: [m],
        schools: (getManagerSchools(m.id) || []).map(sc => ({ id: sc.id, name: sc.school })),
        assignments: m.id_teacher
          ? classSubjects
              .filter(cs => cs.id_teacher === m.id_teacher && cs.is_active)
              .map(cs => ({
                id_class: cs.id_class,
                class_name: cs.class_name,
                id_subject: cs.id_subject,
                subject_name: cs.subject_name,
              }))
          : [],
        canTeach: m.role === 'professor',
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managers, employees, assignments, classSubjects]);

  const rowsFiltradas = useCallback((rows) => {
    const termo = normalizeText(busca);
    if (!termo) return rows;
    return rows.filter(r => normalizeText(`${r.name} ${r.detail} ${r.roleLabel}`).includes(termo));
  }, [busca]);

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Atribuições..."
        size="xl"
        animationType="spin-pulse"
      />
    );
  }

  return (
    <>
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
            Técnico Referência do Município
          </h1>
          <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Vincule funcionários e supervisores às escolas e atribua as turmas
            e disciplinas que cada um leciona. O funcionário não precisa ter
            login no sistema para receber turma.
          </p>
        </div>
      </header>

      {/* Mensagens de feedback */}
      {error && (
        <div className="mb-4 p-4 rounded-xl bg-sideb-red/10 border border-sideb-red/20 text-sideb-red">
          {error}
          <button onClick={() => setError(null)} className="float-right font-bold">×</button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 rounded-xl bg-sideb-blue/10 border border-sideb-blue/20 text-sideb-blue">
          {success}
          <button onClick={() => setSuccess(null)} className="float-right font-bold">×</button>
        </div>
      )}

      {/* Busca */}
      <div className="mb-4">
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome, matrícula, cargo ou e-mail..."
          className={`w-full sm:max-w-md px-4 py-2.5 rounded-xl text-sm border focus:outline-none focus:ring-2 focus:ring-sideb-blue/40 ${darkMode
            ? 'bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-500'
            : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400'
            }`}
        />
      </div>

      {/* Funcionários que podem receber turma */}
      <div className={`rounded-2xl p-6 ${darkMode
        ? 'bg-slate-800/70 border border-slate-700/10'
        : 'bg-white/90 border border-slate-200 shadow-sm'
        }`}>
        <div className="mb-4">
          <h2 className={`text-lg font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            Funcionários ({rowsFiltradas(employeeRows).length})
          </h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Todos os funcionários da rede que podem receber turma e disciplina —
            ter login no sistema não é necessário para isso.
          </p>
        </div>

        {rowsFiltradas(employeeRows).length === 0 ? (
          <div className={`text-center py-12 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <p className="text-lg mb-2">Nenhum funcionário encontrado</p>
            <p className="text-sm">
              {busca
                ? 'Ajuste a busca para encontrar o funcionário.'
                : 'Cadastre o funcionário em RH — Funcionários, com cargo e escola.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {rowsFiltradas(employeeRows).map((row) => (
              <AssignmentRow
                key={row.key}
                row={row}
                canEdit={canEdit}
                darkMode={darkMode}
                onOpen={handleOpenModal}
                onRemoveSchool={handleRemoveSchool}
              />
            ))}
          </div>
        )}
      </div>

      {/* Contas de acesso sem cadastro de funcionário */}
      {rowsFiltradas(userRows).length > 0 && (
        <div className={`mt-6 rounded-2xl p-6 ${darkMode
          ? 'bg-slate-800/70 border border-slate-700/10'
          : 'bg-white/90 border border-slate-200 shadow-sm'
          }`}>
          <div className="mb-4">
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Contas sem cadastro de funcionário ({rowsFiltradas(userRows).length})
            </h2>
            <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Logins de supervisor ou professor que ainda não estão ligados a um
              cadastro do RH. Vincule o cadastro para poder atribuir turmas.
            </p>
          </div>
          <div className="space-y-4">
            {rowsFiltradas(userRows).map((row) => (
              <AssignmentRow
                key={row.key}
                row={row}
                canEdit={canEdit}
                darkMode={darkMode}
                onOpen={handleOpenModal}
                onRemoveSchool={handleRemoveSchool}
              />
            ))}
          </div>
        </div>
      )}

      {/* Modal de Atribuição */}
      {showModal && selectedRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50">
          <div className={`w-full max-w-2xl max-h-[92vh] sm:max-h-[85vh] overflow-hidden rounded-2xl flex flex-col ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white border border-slate-200'
            }`}>
            {/* Header do Modal */}
            <div className={`p-4 sm:p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className="flex justify-between items-center">
                <div>
                  <h2 className={`text-xl font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    {isProfessor ? 'Atribuir Escola, Turma e Disciplina' : 'Atribuir Escolas'}
                  </h2>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {selectedRow.roleLabel}: {selectedRow.name}
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className={`p-2 rounded-lg ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Corpo */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              {/* Escolas */}
              <p className={`text-sm font-medium mb-3 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Escolas
              </p>
              <p className={`text-xs mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Selecione as escolas em que {selectedRow.name} atua:
              </p>

              {schools.length === 0 ? (
                <p className={`text-center py-8 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Nenhuma escola disponível
                </p>
              ) : (
                <div className="space-y-2 mb-6">
                  {schools.map((school) => (
                    <label
                      key={school.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${darkMode
                        ? selectedSchools.includes(school.id)
                          ? 'bg-sideb-blue/20 border border-sideb-blue/50'
                          : 'bg-slate-700/50 hover:bg-slate-700 border border-transparent'
                        : selectedSchools.includes(school.id)
                          ? 'bg-sideb-blue/10 border border-sideb-blue/30'
                          : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSchools.includes(school.id)}
                        onChange={() => toggleSchoolSelection(school.id)}
                        className="w-4 h-4 rounded border-slate-300 text-sideb-blue focus:ring-sideb-blue"
                      />
                      <div className="flex-1">
                        <p className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          {school.school}
                        </p>
                        {school.city_name && (
                          <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {school.city_name}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {/* Vínculo com cadastro do RH, para conta de qualquer perfil sem
                  id_teacher. O vínculo pelo CPF é automático; isto cobre quem
                  não tem CPF no login ou no cadastro. */}
              {selectedRow.kind === 'user' && !selectedRow.employeeId && (
                <div className={`mb-6 p-3 rounded-lg border ${
                  darkMode ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'
                }`}>
                  <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                    Vincular cadastro de funcionário
                  </p>
                  <p className={`text-xs mb-2 ${darkMode ? 'text-amber-200/80' : 'text-amber-700/80'}`}>
                    Esta conta ainda não está ligada a um cadastro do RH. Selecione o cadastro correspondente para que esta pessoa apareça numa linha só e a lotação do RH valha também no login.
                  </p>
                  <SearchableSelect
                    value={selectedTeacherId || ''}
                    onChange={setSelectedTeacherId}
                    options={teachers.map(t => ({
                      value: t.id,
                      label: `${t.teacher_name}${t.teacher_serial ? ` • Matrícula ${t.teacher_serial}` : ''}`,
                    }))}
                    emptyOptionLabel="Selecione o cadastro do funcionário..."
                    searchPlaceholder="Buscar por nome ou matrícula..."
                    darkMode={darkMode}
                  />
                </div>
              )}

              {/* Turmas + Disciplinas (apenas para professor) */}
              {isProfessor && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Turmas e Disciplinas
                    </p>
                    <button
                      onClick={addTeachingRow}
                      disabled={selectedSchools.length === 0 || !effectiveTeacherId}
                      className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                        selectedSchools.length === 0 || !effectiveTeacherId
                          ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                          : darkMode
                            ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      + Adicionar
                    </button>
                  </div>
                  <p className={`text-xs mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Defina cada turma e disciplina que o professor leciona. Ele só poderá preencher o diário dessas combinações.
                  </p>

                  {selectedSchools.length === 0 ? (
                    <p className={`text-sm italic ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Selecione ao menos uma escola para adicionar turmas.
                    </p>
                  ) : !effectiveTeacherId ? (
                    <p className={`text-sm italic ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Selecione o cadastro de funcionário acima para liberar a atribuição.
                    </p>
                  ) : teachingAssignments.length === 0 ? (
                    <p className={`text-sm italic ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Nenhuma turma/disciplina atribuída. Clique em "+ Adicionar".
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {teachingAssignments.map((ta, idx) => (
                        <div
                          key={idx}
                          className={`grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-center p-2 rounded-lg ${
                            darkMode ? 'bg-slate-700/40' : 'bg-slate-50'
                          }`}
                        >
                          <SearchableSelect
                            className="col-span-2 sm:col-span-1"
                            value={ta.id_class || ''}
                            onChange={(v) => updateTeachingRow(idx, 'id_class', v)}
                            options={availableClasses.map(c => ({
                              value: c.id,
                              label: `${c.class_name}${c.school_name ? ` • ${c.school_name}` : ''}`,
                            }))}
                            emptyOptionLabel="Selecione a turma..."
                            searchPlaceholder="Buscar turma ou escola..."
                            darkMode={darkMode}
                          />
                          <SearchableSelect
                            value={ta.id_subject || ''}
                            onChange={(v) => updateTeachingRow(idx, 'id_subject', v)}
                            options={subjects.map(s => ({ value: s.id, label: s.subject_name }))}
                            emptyOptionLabel="Selecione a disciplina..."
                            searchPlaceholder="Buscar disciplina..."
                            darkMode={darkMode}
                          />
                          <button
                            onClick={() => removeTeachingRow(idx)}
                            className={`p-2 rounded hover:text-sideb-red transition-colors ${
                              darkMode ? 'text-slate-400' : 'text-slate-500'
                            }`}
                            title="Remover"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer do Modal */}
            <div className={`p-4 sm:p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className="flex flex-wrap justify-between items-center gap-3">
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {selectedSchools.length} escola(s)
                  {isProfessor && ` • ${teachingAssignments.length} turma(s)/disciplina(s)`}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleCloseModal}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode
                      ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveAssignments}
                    disabled={saving}
                    className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-all ${saving
                      ? 'bg-slate-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-sideb-blue to-sideb-green hover:shadow-lg'
                      }`}
                  >
                    {saving ? 'Salvando...' : 'Salvar Atribuições'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Uma linha da tela de atribuição: funcionário do RH (com ou sem login) ou
 * conta de acesso ainda sem cadastro de funcionário.
 */
function AssignmentRow({ row, canEdit, darkMode, onOpen, onRemoveSchool }) {
  return (
    <div
      className={`p-4 rounded-xl border ${darkMode
        ? 'bg-slate-700/50 border-slate-600/50'
        : 'bg-slate-50 border-slate-200'
        }`}
    >
      <div className="flex justify-between items-start mb-3 gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              {row.name}
            </h3>
            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded font-semibold ${darkMode
              ? 'bg-emerald-500/20 text-emerald-300'
              : 'bg-emerald-100 text-emerald-700'
              }`}>
              {row.roleLabel}
            </span>
            {row.accounts.length > 0 ? (
              row.accounts.map(conta => (
                <span
                  key={conta.id}
                  title={`Acesso @${conta.username}${conta.is_active === false ? ' (inativo)' : ''}`}
                  className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded font-semibold ${conta.is_active === false ? 'line-through opacity-60 ' : ''}${darkMode
                    ? 'bg-purple-500/20 text-purple-300'
                    : 'bg-purple-100 text-purple-700'
                    }`}
                >
                  {conta.role_display || conta.role}
                </span>
              ))
            ) : (
              <span
                title="Funcionário sem login. Pode receber turma normalmente; o login só é necessário para ele entrar no sistema."
                className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded font-semibold ${darkMode
                  ? 'bg-slate-600/60 text-slate-300'
                  : 'bg-slate-200 text-slate-600'
                  }`}
              >
                Sem acesso
              </span>
            )}
          </div>
          {row.detail && (
            <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {row.detail}
            </p>
          )}
        </div>
        {canEdit && (
          <button
            onClick={() => onOpen(row)}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-sideb-blue to-sideb-green hover:shadow-lg transition-all whitespace-nowrap"
          >
            {row.canTeach ? 'Gerenciar Atribuição' : 'Gerenciar Escolas'}
          </button>
        )}
      </div>

      {/* Escolas */}
      <div>
        <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Escolas ({row.schools.length}):
        </p>
        {row.schools.length === 0 ? (
          <p className={`text-sm italic ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            Nenhuma escola vinculada
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {row.schools.map((school) => (
              <span
                key={school.id}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${darkMode
                  ? 'bg-sideb-blue/20 text-sideb-blue'
                  : 'bg-sideb-blue/10 text-sideb-blue'
                  }`}
              >
                {school.name}
                {canEdit && (
                  <button
                    onClick={() => onRemoveSchool(row, school.id)}
                    className="ml-1 hover:text-sideb-red transition-colors"
                    title="Remover escola"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Turmas e disciplinas */}
      {row.canTeach && (
        <div className="mt-3">
          <p className={`text-xs font-medium mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Turmas e Disciplinas ({row.assignments.length}):
          </p>
          {row.assignments.length === 0 ? (
            <p className={`text-sm italic ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Nenhuma turma/disciplina atribuída
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {row.assignments.map((a) => (
                <span
                  key={`${a.id_class}-${a.id_subject}`}
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${darkMode
                    ? 'bg-emerald-500/15 text-emerald-300'
                    : 'bg-emerald-50 text-emerald-700'
                    }`}
                >
                  {a.class_name} • {a.subject_name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default GerentesEscolas;
